import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import PracticePlanModal from './PracticePlanModal.jsx';
import './PracticeLogistics.css';

const LEVEL_COLORS = ['#667eea', '#4ECDC4', '#FF6B6B', '#F7A440', '#9D84B7', '#27AE60', '#E67E22', '#2a9d8f'];
const colorFor = (id) => LEVEL_COLORS[Math.abs(Number(id) || 0) % LEVEL_COLORS.length];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function PracticeLogistics() {
  const toast = useToast();
  const [classes, setClasses] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(iso(new Date()));
  const [weekAnchor, setWeekAnchor] = useState(new Date());
  const [openPlan, setOpenPlan] = useState(null); // { classId, className, date }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [classesRes, athletesRes] = await Promise.all([
        api.get(`${API_BASE_URL}/api/classes?active_only=true`),
        api.get(`${API_BASE_URL}/athletes`),
      ]);
      setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
      setAthletes(Array.isArray(athletesRes.data) ? athletesRes.data : []);
    } catch (err) {
      console.error('Error loading practice logistics:', err);
      toast?.error?.('Failed to load classes');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // 7 days of the anchored week (Mon–Sun)
  const weekDays = () => {
    const base = new Date(weekAnchor);
    const monday = new Date(base);
    monday.setDate(base.getDate() - ((base.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  };

  const shiftWeek = (delta) => {
    const d = new Date(weekAnchor);
    d.setDate(d.getDate() + delta * 7);
    setWeekAnchor(d);
  };

  const todayIso = iso(new Date());
  const selectedLabel = new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  if (loading) {
    return <div className="pl-loading"><div className="pl-spinner" />Loading practice logistics…</div>;
  }

  return (
    <div className="pl-page">
      <div className="pl-header">
        <div>
          <h1 className="pl-title">Practice Logistics</h1>
          <p className="pl-subtitle">Pick a day and a class to design rotations, assign coaches, and set the theme, goals &amp; workout</p>
        </div>
      </div>

      {/* Week strip */}
      <div className="pl-week">
        <button className="pl-week-nav" onClick={() => shiftWeek(-1)}>◀</button>
        <div className="pl-week-days">
          {weekDays().map(d => {
            const di = iso(d);
            const isSel = di === selectedDate;
            const isToday = di === todayIso;
            return (
              <button
                key={di}
                className={`pl-day ${isSel ? 'active' : ''} ${isToday ? 'today' : ''}`}
                onClick={() => setSelectedDate(di)}
              >
                <span className="pl-day-name">{d.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                <span className="pl-day-num">{d.getDate()}</span>
                {isToday && <span className="pl-day-dot" />}
              </button>
            );
          })}
        </div>
        <button className="pl-week-nav" onClick={() => shiftWeek(1)}>▶</button>
      </div>

      <div className="pl-selected-banner">
        Planning for <strong>{selectedLabel}</strong>
        <button className="pl-jump-today" onClick={() => { setWeekAnchor(new Date()); setSelectedDate(todayIso); }}>
          Today
        </button>
      </div>

      {/* Class cards */}
      {classes.length === 0 ? (
        <div className="pl-empty">
          <div className="pl-empty-icon">🤸</div>
          <h3>No active classes</h3>
          <p>Create a class first, then come back to plan its practices.</p>
        </div>
      ) : (
        <div className="pl-class-grid">
          {classes.map(c => {
            const color = colorFor(c.id);
            return (
              <button
                key={c.id}
                className="pl-class-card"
                onClick={() => setOpenPlan({ classId: c.id, className: c.class_name, date: selectedDate })}
              >
                <div className="pl-class-bar" style={{ background: color }} />
                <div className="pl-class-body">
                  <div className="pl-class-head">
                    <h3>{c.class_name}</h3>
                    {c.level && <span className="pl-level" style={{ background: `${color}18`, color }}>{c.level}</span>}
                  </div>
                  <div className="pl-class-meta">
                    {c.coach_name && <span>👤 {c.coach_name}</span>}
                    <span>🤸 {c.current_enrollment || 0} athletes</span>
                  </div>
                  <div className="pl-class-cta" style={{ color }}>
                    📋 Open practice plan →
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {openPlan && (
        <PracticePlanModal
          classId={openPlan.classId}
          practiceDate={openPlan.date}
          className={openPlan.className}
          athletes={athletes}
          onClose={() => setOpenPlan(null)}
        />
      )}
    </div>
  );
}

export default PracticeLogistics;
